import { ArrowBack } from "@mui/icons-material";
import { Avatar, Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Tooltip } from "@mui/material";
import { useAtom } from "jotai";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { complaintEdit, permissionsAtom } from "../../atoms/atom";
import AppBar from "../../components/app-bar/AppBar";
import { ComplaintService } from "../../services/ComplaintService";
import { ComplaintDTO, priorityId } from "../../types/ComplaintDTO";
import { PdfService } from "../../utils/PdfService";
import { useUtils } from "../../utils/utilFunctions";
import { complaintPriorities } from "../complaint/Complaint";
import { getLoading } from "../execution-events/ExecutionEvents";
import { obtemIconeOrdem } from "../executions/Executions";
import './ComplaintPanel.scss';

const ComplaintPanel: FC = () => {
	const [, setSelectedComplaint] = useAtom(complaintEdit);
	const [search, setSearch] = useState('');
	const [complaints, setComplaints] = useState<ComplaintDTO[]>([]);
	const [companyFilter, setCompanyFilter] = useState('Todas');
	const [statusFilter, setStatusFilter] = useState('Todas');
	const [priorityFilter, setPriorityFilter] = useState('Todas');
	const [fieldOrder, setFieldOrder] = useState('ID');
	const [ordemAsc, setOrdemAsc] = useState(false);
	const [loading, setLoading] = useState(true);
	const [permissions] = useAtom(permissionsAtom);
	const navigate = useNavigate();
	const { formatStringFullDate, verifyPermission } = useUtils();
	const service = new ComplaintService();
	const pdfService = new PdfService();

	const companyNames = Array.isArray(complaints)
		? [...new Set(complaints.map(item => item.companyName))]
		: [];

	function mudaOrdem(field: string) {
		setFieldOrder(field);
		setOrdemAsc(!ordemAsc);
	}

	const openComplaint = (v: ComplaintDTO) => {
		setSelectedComplaint(v);
		navigate(`/gestao-de-denuncias/painel/${v.id}`);
	}

	const handleExportPdf = () => {
		pdfService.exportComplaintsToPdf(filterComplaints());
	}

	useEffect(() => {
		const loadComplaints = async () => {
			const response = await service.getComplaints();
			if (response && response.data) {
				setComplaints(response.data);
			}
			setLoading(false);
		};
		loadComplaints();
		if (permissions.length > 0) {
			verifyPermission(permissions, 'COMPLAINTS');
		}
	}, []);

	return (
		<>
			<AppBar
				modulo="Gestão de denúncias - Painel"
			/>
			<div className="complaint-panel">
				<div className="top">
					<div className="document-inputs">
						{permissions.includes('MANAGEMENT_COMPLAINTS') &&
							<Box flex={1}>
								<Button
									variant="contained"
									color="success"
									id="group-search"
									onClick={() => navigate('/gestao-de-denuncias')}
									startIcon={<ArrowBack />}
								>
									Voltar para empresas
								</Button>
							</Box>
						}
						{permissions.includes('EXPORT_COMPLAINTS') &&
							<Button
								variant="contained"
								color="secondary"
								id="export-pdf"
								onClick={handleExportPdf}
							>
								Exportar denúncias filtradas
							</Button>}
						<TextField
							sx={{width: '190px'}}
							label="Buscar por protocolo"
							id="group-search"
							size="small"
							value={search}
							onChange={(e) => setSearch(e.target.value)}
						/>
						<FormControl size="small" sx={{width: "330px"}}>
							<InputLabel>
								Filtrar por empresa
							</InputLabel>
							<Select
								label='Filtrar por empresa'
								id="select-empresa"
								value={companyFilter}
								onChange={(e) => setCompanyFilter(e.target.value)}
							>
								{companyNames.map(item => (
									<MenuItem
										key={item}
										value={item}
									>
										{item}
									</MenuItem>
								))}
								<MenuItem
									key={'Todas'}
									value={'Todas'}
								>
									Todas
								</MenuItem>
							</Select>
						</FormControl>
						<FormControl size="small" sx={{width: "150px"}}>
							<InputLabel>
								Filtrar por status
							</InputLabel>
							<Select
								label='Filtrar por status'
								id="select-status"
								value={statusFilter}
								onChange={(e) => setStatusFilter(e.target.value)}
							>
								<MenuItem
									key={'Todas'}
									value={'Todas'}
								>
									Todas
								</MenuItem>
								<MenuItem
									key={'Aberta'}
									value={'Aberta'}
								>
									Abertas
								</MenuItem>
								<MenuItem
									key={'Em análise'}
									value={'Em análise'}
								>
									Em análise
								</MenuItem>
								<MenuItem
									key={'Finalizada'}
									value={'Finalizada'}
								>
									Finalizadas
								</MenuItem>
							</Select>
						</FormControl>
						<FormControl size="small" sx={{width: "150px"}}>
							<InputLabel>
								Filtrar por prioridade
							</InputLabel>
							<Select
								label='Filtrar por prioridade'
								id="select-prioridade"
								value={priorityFilter}
								onChange={(e) => setPriorityFilter(e.target.value)}
							>
								<MenuItem
									key={'Todas'}
									value={'Todas'}
								>
									Todas
								</MenuItem>
								{complaintPriorities.map((item) => (
									<MenuItem
										key={item}
										value={item}
									>
										{item}s
									</MenuItem>
								))}
							</Select>
						</FormControl>
						{companyFilter !== 'Todas' || statusFilter !== 'Todas' || priorityFilter !== 'Todas'
							? <Button
								variant='contained'
								color='error'
								onClick={() => {
									setCompanyFilter('Todas');
									setStatusFilter('Todas');
									setPriorityFilter('Todas');
								}}
							>
								Limpar filtros
							</Button>
							: <></>
						}
					</div>
				</div>
					{!loading && filterComplaints().length
						? <div className="tabela">
							<div className="header-tabela">
								<p className="pointer" id="company-header" onClick={() => mudaOrdem('COMPANY')}>Empresa {obtemIconeOrdem('NAME', fieldOrder, ordemAsc)}</p>
								<p>Protocolo</p>
								<p className="pointer" onClick={() => mudaOrdem('DATE')}>Registrada em {obtemIconeOrdem('DATE', fieldOrder, ordemAsc)}</p>
								<p className="pointer" onClick={() => mudaOrdem('TYPE')}>Tipo informado {obtemIconeOrdem('TYPE', fieldOrder, ordemAsc)}</p>
								<p className="pointer" onClick={() => mudaOrdem('PRIORITY')}>Prioridade {obtemIconeOrdem('PRIORITY', fieldOrder, ordemAsc)}</p>
								<p>Responsáveis</p>
								<p className="pointer" onClick={() => mudaOrdem('STATUS')}>Status {obtemIconeOrdem('STATUS', fieldOrder, ordemAsc)}</p>
							</div>
							<div className="body-table">
								{filterComplaints().sort((a, b) => {
									let valueA: string | number;
									let valueB: string | number;
									switch (fieldOrder) {
										case 'DATE':
											valueA = new Date(a.createdAt).getTime();
											valueB = new Date(b.createdAt).getTime();
											break;
										case 'COMPANY':
											valueA = a.companyName;
											valueB = b.companyName;
											break;
										case 'TYPE':
											valueA = a.complaintType;
											valueB = b.complaintType;
											break;
										case 'STATUS':
											valueA = a.status;
											valueB = b.status;
											break;
										case 'PRIORITY':
											valueA = priorityId(a.priority);
											valueB = priorityId(b.priority);
											break;
										default:
											valueA = a.id;
											valueB = b.id;
									}
									if (typeof valueA === 'string' && typeof valueB === 'string') {
										return ordemAsc
											? valueA.localeCompare(valueB)
											: valueB.localeCompare(valueA);
									} else {
										return ordemAsc
											? (valueA as number) - (valueB as number)
											: (valueB as number) - (valueA as number);
									}
								}).map((complaint) => (
									<div id={complaint.id + ''} className="linha-empresa pointer" onClick={() => openComplaint(complaint)}>
										<p id="company-header"><span>{complaint.companyName}</span></p>
										<p>{complaint.id}</p>
										<p>{formatStringFullDate(complaint.createdAt)}</p>
										<p>{complaint.complaintType}</p>
										<Box flex={1}>
											<p className={`${complaint.priority} color bold`}>{complaint.priority}</p>
										</Box>
										<p>
											{complaint.actions && complaint.actions.length
												? [...new Set(complaint.actions?.filter(action => !!action.name)
													.map(item => item.name)
												)]
													.map(name => (
														<Tooltip title={name}>
															<Avatar sx={{ bgcolor: '#5872e6' }}>{name[0].toUpperCase()}</Avatar>
														</Tooltip>
													))
												: "Nenhum"
											}
										</p>
										<Box flex={1}>
											<p className={`${complaint.status === 'Em análise' ? 'Média' : complaint.status} color bold`}>
												{complaint.status}
											</p>
										</Box>
									</div>
								))}
							</div>
						</div>
						: loading
							? getLoading()
							: <div className="sem-execucoes">
								Nenhuma registro para os filtros atuais...
							</div>
					}
			</div>
		</>
	);

	function filterComplaints() {
		let filtered = [...complaints];
		if (search) {
			filtered = filtered.filter(item => item.id.toString() === search);
		}
		if (companyFilter !== "Todas") {
			filtered = filtered.filter(item => item.companyName === companyFilter);
		}
		if (statusFilter !== "Todas") {
			filtered = filtered.filter(item => item.status === statusFilter);
		}
		if (priorityFilter !== "Todas") {
			filtered = filtered.filter(item => item.priority === priorityFilter);
		}
		return filtered;
	}
}

export default ComplaintPanel;